<div class="inbox-head" aria-label="Agent Cases controls">
  <div class="inbox-head__title-wrap">
    <h2 class="inbox-head__title">Agent Cases</h2>
    <span class="inbox-head__count">{{ countLabel() }}</span>
    @if (casesLoading()) {
      <span class="inbox-head__loading" role="status">
        <span class="inbox-head__spinner" aria-hidden="true"></span>
        Loading more cases…
      </span>
    }
  </div>

  <div class="inbox-head__tools">
    <button
      type="button"
      class="inbox-head__tool inbox-head__tool--refresh"
      title="Reset filters"
      aria-label="Reset filters"
      (click)="onResetFilters()">
      <i class="pi pi-refresh" aria-hidden="true"></i>
    </button>
    <div class="inbox-head__sort-wrap">
      <button
        type="button"
        class="inbox-head__tool"
        [class.inbox-head__tool--active]="sortMenuOpen"
        title="Sort exceptions"
        aria-label="Sort exceptions"
        (click)="onToggleSortOrder($event)">
        <i class="pi pi-sort-alt" aria-hidden="true"></i>
      </button>

      @if (sortMenuOpen) {
        <div class="inbox-sort-menu" role="menu" aria-label="Sort options">
          @for (option of sortOptions; track option.value) {
            <button
              type="button"
              class="inbox-sort-menu__item"
              [class.inbox-sort-menu__item--active]="selectedSortOption() === option.value"
              [attr.aria-checked]="selectedSortOption() === option.value"
              role="menuitemradio"
              (click)="onSortOptionSelected(option.value)">
              {{ option.label }}
            </button>
          }
        </div>
      }
    </div>
    <div class="inbox-head__filter-wrap">
      <button
        type="button"
        class="inbox-head__tool"
        [class.inbox-head__tool--active]="hasAdvancedFilters() || filterModalOpen"
        title="Open filters"
        aria-label="Open filters"
        (click)="onOpenFilterModal($event)">
        <i class="pi pi-filter" aria-hidden="true"></i>
      </button>

      @if (filterModalOpen) {
        <app-workflow-inbox-filter-modal
          [initialSearchText]="selectedSearchText()"
          [initialAsset]="selectedAsset()"
          [initialTimeline]="selectedTimeline()"
          [initialDate]="selectedDate()"
          [initialRunForDate]="selectedRunForDate()"
          [assetOptions]="assets()"
          [initialExceptionType]="selectedExceptionType()"
          [initialWorkflowStatus]="selectedWorkflowStatus()"
          [initialMyPendingActions]="selectedMyPendingActions()"
          [initialPriority]="selectedPriority()"
          [exceptionTypeOptions]="exceptionTypeOptions()"
          [priorityOptions]="priorityOptions()"
          (applied)="onFilterApplied($event)"
          (cancelled)="onFilterCancelled()" />
      }
    </div>
  </div>
</div>

<div class="inbox-list" cdkScrollable (scroll)="hidePriorityTooltip()">
  @for (item of displayItems(); track item.id) {
    <article
      class="inbox-item"
      [class.inbox-item--new]="item.badge === WorkflowBadgeStatus.New"
      [class.inbox-item--waiting]="item.badge === WorkflowBadgeStatus.AwaitingDecision"
      [class.inbox-item--active]="isSelected(item)"
      [class.inbox-item--well-exception]="isWellException(item)"
      [class.inbox-item--product-deviation]="isProductDeviation(item)"
      [class.inbox-item--prio-high]="priorityLevel(item) === 'high'"
      [class.inbox-item--prio-medium]="priorityLevel(item) === 'medium'"
      [class.inbox-item--prio-low]="priorityLevel(item) === 'low'">
      <button
        type="button"
        class="inbox-item__select"
        [attr.aria-current]="isSelected(item) ? 'true' : null"
        [attr.aria-describedby]="priorityLevel(item) ? 'priority-description-' + item.id : null"
        (focus)="showPriorityTooltip(item)"
        (blur)="hidePriorityTooltip()"
        (mouseleave)="deferHidePriorityTooltip()"
        (click)="onItemClick(item)">
        <div class="inbox-item__inner">
        <div class="item-top-row">
          <div class="item-badges">
            @if (priorityLevel(item); as prio) {
              <span
                class="item-priority item-priority--{{ prio }}"
                cdkOverlayOrigin
                #priorityOrigin="cdkOverlayOrigin"
                (mouseenter)="showPriorityTooltip(item)">
                {{ getPriorityLabel(item) }}
              </span>
              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="priorityOrigin"
                [cdkConnectedOverlayOpen]="priorityTooltipItemId() === item.id && badgeTooltipKind() === 'priority'"
                [cdkConnectedOverlayPush]="true"
                [cdkConnectedOverlayPositions]="[
                  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
                  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
                ]"
                (detach)="onPriorityTooltipDetached(item)">
                <span
                  class="badge-tooltip"
                  role="tooltip"
                  (mouseenter)="showPriorityTooltip(item)"
                  (mouseleave)="deferHidePriorityTooltip()"><strong>{{ priorityTooltipHeading(item) }}</strong> {{ priorityTooltipDescription(item) }}</span>
              </ng-template>
            }
            @if (isWellException(item) || isProductDeviation(item)) {
              <span
                class="item-case-type item-priority"
                cdkOverlayOrigin
                #caseTypeOrigin="cdkOverlayOrigin"
                (mouseenter)="showBadgeTooltip(item, 'case-type')">
                <span aria-hidden="true">{{ isWellException(item) ? 'WE' : 'PD' }}</span>
                <span class="visually-hidden">{{ isWellException(item) ? 'Well Exception' : 'Product Deviation' }}.</span>
              </span>
              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="caseTypeOrigin"
                [cdkConnectedOverlayOpen]="priorityTooltipItemId() === item.id && badgeTooltipKind() === 'case-type'"
                [cdkConnectedOverlayPush]="true"
                [cdkConnectedOverlayPositions]="[
                  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
                  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
                ]"
                (detach)="onPriorityTooltipDetached(item, 'case-type')">
                <span
                  class="badge-tooltip"
                  role="tooltip"
                  (mouseenter)="showBadgeTooltip(item, 'case-type')"
                  (mouseleave)="deferHidePriorityTooltip()">{{ isWellException(item) ? 'Well Exception' : 'Product Deviation' }}</span>
              </ng-template>
            }
            @if (workflowIndicator(item); as indicator) {
              <span
                class="item-new-indicator"
                [class.item-new-indicator--claimed]="indicator === 'claimed'"
                [class.item-new-indicator--done]="indicator === 'done'"
                cdkOverlayOrigin
                #workflowOrigin="cdkOverlayOrigin"
                (mouseenter)="showBadgeTooltip(item, 'workflow')"
                aria-hidden="true"></span>
              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="workflowOrigin"
                [cdkConnectedOverlayOpen]="priorityTooltipItemId() === item.id && badgeTooltipKind() === 'workflow'"
                [cdkConnectedOverlayPush]="true"
                [cdkConnectedOverlayPositions]="[
                  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
                  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
                ]"
                (detach)="onPriorityTooltipDetached(item, 'workflow')">
                <span
                  class="badge-tooltip"
                  role="tooltip"
                  (mouseenter)="showBadgeTooltip(item, 'workflow')"
                  (mouseleave)="deferHidePriorityTooltip()"><strong>{{ workflowStatusLabel(item) }}</strong>@if (workflowTooltipDetail(item); as detail) { - {{ detail }}}</span>
              </ng-template>
              <span class="visually-hidden">{{ indicator === 'new' ? 'Open workflow.' : indicator === 'done' ? 'Workflow closed.' : 'Workflow in progress.' }} {{ workflowTooltip(item) }}.</span>
            } @else if (!isWellException(item) && !isProductDeviation(item) && item.badge !== WorkflowBadgeStatus.New) {
              <span class="item-badge {{ getBadgeClass(item.badge) }}">
                {{ getBadgeLabel(item.badge) }}
              </span>
            }
          </div>
          <div class="item-dates">
            <span class="visually-hidden">{{ getReportDateLabel(item) + ': ' }}</span>
            <span
              class="item-date item-date--primary"
              cdkOverlayOrigin
              #runForDateOrigin="cdkOverlayOrigin"
              (mouseenter)="showBadgeTooltip(item, 'run-for-date')">{{ getReportDate(item) }}</span>
            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="runForDateOrigin"
              [cdkConnectedOverlayOpen]="priorityTooltipItemId() === item.id && badgeTooltipKind() === 'run-for-date'"
              [cdkConnectedOverlayPush]="true"
              [cdkConnectedOverlayPositions]="[
                { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
                { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 }
              ]"
              (detach)="onPriorityTooltipDetached(item, 'run-for-date')">
              <span
                class="badge-tooltip"
                role="tooltip"
                (mouseenter)="showBadgeTooltip(item, 'run-for-date')"
                (mouseleave)="deferHidePriorityTooltip()"><strong>{{ getReportDateLabel(item) }}:</strong> {{ getReportDate(item) }}</span>
            </ng-template>
          </div>
        </div>

        <span class="item-title">{{ item.title }}</span>

        </div>
      </button>
      @if (priorityLevel(item)) {
        <span class="visually-hidden" [id]="'priority-description-' + item.id">{{ priorityTooltip(item) }}</span>
      }

      <details class="inbox-item__details">
        <summary class="inbox-item__details-summary">
          Details
          <i class="pi pi-chevron-down" aria-hidden="true"></i>
        </summary>
        <div class="inbox-item__details-content">
          @if (!item.persistedCase) { <span class="item-reference">{{ item.exceptionReferenceId || item.id }}</span> }
          <span
            class="item-context"
            tabindex="0"
            cdkOverlayOrigin
            #contextOrigin="cdkOverlayOrigin"
            (mouseenter)="showBadgeTooltip(item, 'context')"
            (mouseleave)="deferHidePriorityTooltip()"
            (focus)="showBadgeTooltip(item, 'context')"
            (blur)="hidePriorityTooltip()">{{ getContextLine(item) }}</span>
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="contextOrigin"
            [cdkConnectedOverlayOpen]="priorityTooltipItemId() === item.id && badgeTooltipKind() === 'context'"
            [cdkConnectedOverlayPush]="true"
            [cdkConnectedOverlayPositions]="[
              { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
              { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
            ]"
            (detach)="onPriorityTooltipDetached(item, 'context')">
            <span
              class="badge-tooltip"
              role="tooltip"
              (mouseenter)="showBadgeTooltip(item, 'context')"
              (mouseleave)="deferHidePriorityTooltip()">{{ getContextLine(item) }}</span>
          </ng-template>


        <div class="item-meta">
          <div class="item-meta__field item-meta__field--type">
            <span class="item-meta__label">Type</span>
            <span class="item-meta__value">{{ getTypeLabel(item) }}</span>
          </div>
          @if (!isWellException(item)) {
          <div class="item-meta__field item-meta__field--conf">
            <span class="item-meta__label">Confidence</span>
            <span class="item-meta__value item-meta__value--conf">{{ confidenceScore(item) === null ? 'Not recorded' : getConfidenceLabel(item) }}</span>
          </div>
          }
        </div>
        @if (isProductDeviation(item) && confidenceScore(item) !== null) {
          <div class="item-confidence-details">
            <details class="item-confidence-rubric">
              <summary>How scoring works for confidence</summary>
              <p>Current Product Deviation rubric: evidence support, not a probability that the cause is correct.</p>
              <table>
                <caption>Scoring components</caption>
                <thead>
                  <tr><th scope="col">Points</th><th scope="col">Rule</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>0-54</td>
                    <td><strong>Evidence breadth:</strong> +18 per asset-matched class, up to 3: downtime, work orders, well tests, equipment telemetry, or well telemetry.</td>
                  </tr>
                  <tr>
                    <td>0 or +18</td>
                    <td><strong>Telemetry corroboration:</strong> +18 for asset-matched equipment or well telemetry, in addition to breadth points.</td>
                  </tr>
                  <tr>
                    <td>0-18</td>
                    <td><strong>Production movement:</strong> best affected stream's absolute theoretical change divided by absolute allocated change, capped at 1, multiplied by 18 and rounded.</td>
                  </tr>
                  <tr>
                    <td>-15 each</td>
                    <td><strong>Contradictions:</strong> subtract 15 per recorded contradiction mentioning a candidate asset or stream.</td>
                  </tr>
                </tbody>
              </table>
              <p>Each candidate's total is limited to 0-90; the case takes the highest candidate score. Missing evidence has no separate deduction.</p>
              <p>General guidance only. Weights are not calibrated; older runs may use different rules. This case's point breakdown was not recorded.</p>
            </details>
          </div>
        }
        </div>
      </details>
    </article>
  } @empty {
    @if (selectedMyPendingActions() && !casesLoading() && resultsSettled()) {
      <p role="status">No pending actions assigned to you match the selected filters.</p>
    } @else if (selectedWorkflowStatus() && !casesLoading() && resultsSettled()) {
      <p role="status">No cases match the selected workflow status.</p>
    }
  }
</div>
